/**
 * Lender pipelines — the company's loans as each lender's own portal lists them (Spring EQ's
 * EMMA, PennyMac's POWER+), read every hour by the worker and normalised: one table, a change
 * feed, a detail drawer with everything the portal sent, and "Sync now".
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { LENDER_LOAN_EVENT_LABELS, LENDER_LOCK_LABELS, LENDER_STAGE_LABELS, LENDER_STAGE_ORDER, MANA_SOURCE_LABELS, type LenderLoanEventType, type LenderLockStatus, type LenderStage, type ManaPricingSource } from '@mana/shared';
import { api, ApiError } from '../../lib/api.js';
import { fmtCT } from '../../lib/time.js';
import { Button } from '../../ui/Button.js';
import { Input, Select } from '../../ui/Input.js';
import { Badge, Card, CardHeader } from '../../ui/Card.js';

interface LenderLoanRow {
  id: string;
  lenderKey: string;
  externalId: string;
  loanNumber: string | null;
  borrowerName: string | null;
  coBorrowerName: string | null;
  propertyAddress: string | null;
  propertyCity: string | null;
  propertyState: string | null;
  propertyZip: string | null;
  loanAmount: number | null;
  loanRate: number | null;
  ltv: number | null;
  loanProduct: string | null;
  loanPurpose: string | null;
  loanType: string | null;
  occupancy: string | null;
  stage: LenderStage;
  status: string | null;
  statusDetail: string | null;
  statusAt: string | null;
  lockStatus: LenderLockStatus;
  lockStatusRaw: string | null;
  lockDate: string | null;
  lockExpiresAt: string | null;
  closingAt: string | null;
  hasOpenConditions: boolean | null;
  loanOfficer: string | null;
  portalUrl: string | null;
  active: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  updatedAt: string;
}
interface LenderLoanEvent {
  id: string;
  type: LenderLoanEventType;
  fromValue: string | null;
  toValue: string | null;
  detailJson: string | null;
  at: string;
  loan?: { id: string; loanNumber: string | null; borrowerName: string | null; lenderKey: string };
}
interface ListResponse {
  loans: LenderLoanRow[];
  events: LenderLoanEvent[];
}
interface StatusResponse {
  lenders: Array<{
    key: string;
    name: string;
    portal: string;
    login: string;
    configured: boolean;
    checkEveryMinutes: number;
    openLoans: number;
    totalLoans: number;
    lastSyncOkAt: string | null;
    lastRun: { at: string; status: string; loans: number; changes: number; message: string | null; trigger: string } | null;
  }>;
}
interface DetailResponse {
  loan: LenderLoanRow & { raw: Record<string, unknown>; events: LenderLoanEvent[] };
}
interface SyncResponse {
  results: Array<{ lenderKey: string; ok: boolean; result?: { loans: number; created: number; updated: number; gone: number; events: number }; error?: string }>;
  status: StatusResponse;
}

const lenderName = (key: string) => MANA_SOURCE_LABELS[key as ManaPricingSource] ?? key;
const money = (n: number | null) => (n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const pct = (n: number | null, digits = 3) => (n == null ? '—' : `${n.toFixed(digits)}%`);
const dateOnly = (iso: string | null) => (iso ? fmtCT(iso, { dateOnly: true, year: false }) : '—');
const daysUntil = (iso: string | null) => (iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000) : null);

const STAGE_TONE: Record<LenderStage, 'neutral' | 'info' | 'warning' | 'success' | 'gold'> = {
  registered: 'neutral',
  submitted: 'info',
  in_review: 'info',
  approved: 'gold',
  clear_to_close: 'success',
  docs_out: 'success',
  funded: 'success',
  suspended: 'warning',
  denied: 'warning',
  withdrawn: 'neutral',
  unknown: 'neutral',
};

function LockCell({ loan }: { loan: LenderLoanRow }) {
  const days = daysUntil(loan.lockExpiresAt);
  const urgent = loan.lockStatus === 'locked' && days != null && days <= 5;
  return (
    <span className={clsx('whitespace-nowrap', urgent && 'font-semibold text-amber-700', loan.lockStatus === 'expired' && 'text-rose-600')} title={loan.lockStatusRaw ?? undefined}>
      {LENDER_LOCK_LABELS[loan.lockStatus]}
      {loan.lockExpiresAt && (loan.lockStatus === 'locked' || loan.lockStatus === 'expired') ? ` · ${dateOnly(loan.lockExpiresAt)}${urgent ? ` (${days}d)` : ''}` : ''}
    </span>
  );
}

function EventLine({ e, withLoan }: { e: LenderLoanEvent; withLoan?: boolean }) {
  const who = withLoan && e.loan ? `${lenderName(e.loan.lenderKey)} · ${e.loan.borrowerName ?? e.loan.loanNumber ?? '—'}` : null;
  const detail = e.type === 'lock_expiring' ? `lock expires ${e.toValue ?? ''}` : e.fromValue && e.toValue ? `${e.fromValue} → ${e.toValue}` : (e.toValue ?? e.fromValue ?? '');
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 py-1 text-xs">
      <span className="tabular-nums text-slate-400">{fmtCT(e.at)}</span>
      <span className={clsx('font-semibold', e.type === 'lock_expiring' || e.type === 'gone' ? 'text-amber-700' : 'text-brand-navy-700')}>{LENDER_LOAN_EVENT_LABELS[e.type] ?? e.type}</span>
      {who && <span className="text-slate-600">{who}</span>}
      <span className="text-slate-500">{detail}</span>
    </li>
  );
}

function LoanDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery<DetailResponse>({ queryKey: ['lender-loan', id], queryFn: () => api(`/api/pipeline/lender-loans/${encodeURIComponent(id)}`) });
  const loan = q.data?.loan;
  const rows: Array<[string, string]> = loan
    ? [
        ['Lender', lenderName(loan.lenderKey)],
        ['Loan #', loan.loanNumber ?? '—'],
        ['Borrower', [loan.borrowerName, loan.coBorrowerName].filter(Boolean).join(' & ') || '—'],
        ['Property', [loan.propertyAddress, loan.propertyCity, loan.propertyState, loan.propertyZip].filter(Boolean).join(', ') || '—'],
        ['Stage', `${LENDER_STAGE_LABELS[loan.stage]}${loan.status ? ` · ${loan.status}` : ''}${loan.statusDetail ? ` · ${loan.statusDetail}` : ''}`],
        ['Status as of', loan.statusAt ? fmtCT(loan.statusAt) : '—'],
        ['Lock', `${LENDER_LOCK_LABELS[loan.lockStatus]}${loan.lockStatusRaw ? ` (${loan.lockStatusRaw})` : ''}`],
        ['Lock date / expiration', `${dateOnly(loan.lockDate)} → ${dateOnly(loan.lockExpiresAt)}`],
        ['Closing', dateOnly(loan.closingAt)],
        ['Amount / rate / LTV', `${money(loan.loanAmount)} · ${pct(loan.loanRate)} · ${pct(loan.ltv, 2)}`],
        ['Product', [loan.loanProduct, loan.loanType].filter(Boolean).join(' · ') || '—'],
        ['Purpose / occupancy', [loan.loanPurpose, loan.occupancy].filter(Boolean).join(' · ') || '—'],
        ['Open conditions', loan.hasOpenConditions == null ? '—' : loan.hasOpenConditions ? 'Yes' : 'No'],
        ['Loan officer', loan.loanOfficer ?? '—'],
        ['Seen', `first ${fmtCT(loan.firstSeenAt)} · last ${fmtCT(loan.lastSeenAt)}${loan.active ? '' : ' · no longer listed by the portal'}`],
      ]
    : [];
  const rawEntries = loan ? Object.entries(loan.raw).filter(([, v]) => v != null && v !== '' && typeof v !== 'object') : [];
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40" onClick={onClose} role="presentation">
      <aside className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Lender loan detail">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">{loan ? lenderName(loan.lenderKey) : 'Lender loan'}</p>
            <h3 className="mt-1 font-display text-2xl font-bold text-brand-navy-800">{loan?.borrowerName ?? loan?.loanNumber ?? 'Loan'}</h3>
            {loan?.portalUrl && (
              <a href={loan.portalUrl} target="_blank" rel="noreferrer" className="text-xs text-brand-navy-600 underline">
                Open in the lender's portal
              </a>
            )}
          </div>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
        {q.isLoading && <p className="mt-6 text-sm text-slate-500">Loading…</p>}
        {q.error && <p className="mt-6 text-sm text-rose-600">{q.error instanceof ApiError ? q.error.message : 'Could not load the loan.'}</p>}
        {loan && (
          <>
            <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {rows.map(([k, v]) => (
                <div key={k} className="border-b border-slate-100 py-1.5">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{k}</dt>
                  <dd className="mt-0.5 text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
            <h4 className="mt-6 text-sm font-semibold text-brand-navy-800">Changes</h4>
            {loan.events.length ? (
              <ul className="mt-1 divide-y divide-slate-100">
                {loan.events.map((e) => (
                  <EventLine key={e.id} e={e} />
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-xs text-slate-500">No changes recorded yet.</p>
            )}
            <h4 className="mt-6 text-sm font-semibold text-brand-navy-800">Everything the portal sent</h4>
            <dl className="mt-1 grid grid-cols-1 gap-x-6 text-xs sm:grid-cols-2">
              {rawEntries.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1">
                  <dt className="truncate text-slate-500" title={k}>
                    {k}
                  </dt>
                  <dd className="max-w-[60%] truncate text-right text-slate-800" title={String(v)}>
                    {String(v)}
                  </dd>
                </div>
              ))}
            </dl>
          </>
        )}
      </aside>
    </div>
  );
}

export function LenderPipelines({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const [lender, setLender] = useState('');
  const [view, setView] = useState<'open' | 'all'>('open');
  const [stage, setStage] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const params = new URLSearchParams();
  if (lender) params.set('lender', lender);
  params.set('view', view);
  if (stage) params.set('stage', stage);
  if (q.trim()) params.set('q', q.trim());
  const statusQuery = useQuery<StatusResponse>({ queryKey: ['lender-loans-status'], queryFn: () => api('/api/pipeline/lender-loans/status'), refetchInterval: 5 * 60_000 });
  const listQuery = useQuery<ListResponse>({ queryKey: ['lender-loans', params.toString()], queryFn: () => api(`/api/pipeline/lender-loans?${params.toString()}`) });
  const syncMut = useMutation({
    mutationFn: (only?: string) => api<SyncResponse>('/api/pipeline/lender-loans/sync', { method: 'POST', body: JSON.stringify(only ? { lender: only } : {}) }),
    onSuccess: (r) => {
      const parts = r.results.map((x) => (x.ok && x.result ? `${lenderName(x.lenderKey)}: ${x.result.loans} loans, ${x.result.events} change${x.result.events === 1 ? '' : 's'}` : `${lenderName(x.lenderKey)}: ${x.error ?? 'failed'}`));
      setMsg({ tone: r.results.every((x) => x.ok) ? 'ok' : 'err', text: parts.join(' · ') || 'No lender login is saved yet.' });
      void qc.invalidateQueries({ queryKey: ['lender-loans'] });
      void qc.invalidateQueries({ queryKey: ['lender-loans-status'] });
    },
    onError: (e) => setMsg({ tone: 'err', text: e instanceof ApiError ? e.message : 'Sync failed.' }),
  });
  const lenders = statusQuery.data?.lenders ?? [];
  const loans = listQuery.data?.loans ?? [];
  const events = listQuery.data?.events ?? [];
  const configuredCount = lenders.filter((l) => l.configured).length;
  const sorted = useMemo(
    () =>
      [...loans].sort((a, b) => {
        // Locks expiring soonest first, then the most recently changed.
        const ad = a.lockStatus === 'locked' && a.lockExpiresAt ? new Date(a.lockExpiresAt).getTime() : Number.POSITIVE_INFINITY;
        const bd = b.lockStatus === 'locked' && b.lockExpiresAt ? new Date(b.lockExpiresAt).getTime() : Number.POSITIVE_INFINITY;
        if (ad !== bd) return ad - bd;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }),
    [loans],
  );

  return (
    <Card>
      <CardHeader
        title={`Lender pipelines${listQuery.data ? ` (${loans.length})` : ''}`}
        subtitle="Loans as the lenders' own portals list them — Spring EQ (EMMA) and PennyMac (POWER+) — read every hour. Click a row for everything the portal sent."
        actions={
          <Button variant="secondary" disabled={!canEdit || syncMut.isPending || configuredCount === 0} onClick={() => syncMut.mutate(undefined)} title={configuredCount ? 'Read every lender portal now (PennyMac signs in headlessly — a minute or two)' : 'Save a lender portal login under Integrations first'}>
            {syncMut.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
        }
      />
      <div className="border-b border-slate-100 px-5 py-3">
        <ul className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
          {lenders.map((l) => (
            <li key={l.key} className="flex items-center gap-1.5">
              <span className={clsx('inline-block h-2 w-2 rounded-full', !l.configured ? 'bg-slate-300' : l.lastRun?.status === 'error' ? 'bg-rose-500' : l.lastSyncOkAt ? 'bg-emerald-500' : 'bg-amber-400')} />
              <span className="font-semibold text-slate-700">{l.name}</span>
              {!l.configured ? (
                <span>— save the {l.portal} login under {l.login}</span>
              ) : (
                <span title={l.lastRun?.message ?? undefined}>
                  {l.openLoans} open of {l.totalLoans} · {l.lastRun ? `${l.lastRun.status === 'ok' ? 'synced' : 'last sync failed'} ${fmtCT(l.lastRun.at)}` : 'not synced yet'} · every hour
                </span>
              )}
            </li>
          ))}
        </ul>
        {msg && <p className={clsx('mt-2 text-xs', msg.tone === 'ok' ? 'text-emerald-700' : 'text-rose-600')}>{msg.text}</p>}
      </div>
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 px-5 py-3">
        <Select value={lender} onChange={(e) => setLender(e.target.value)} className="w-44" aria-label="Lender">
          <option value="">All lenders</option>
          {lenders.map((l) => (
            <option key={l.key} value={l.key}>
              {l.name}
            </option>
          ))}
        </Select>
        <Select value={view} onChange={(e) => setView(e.target.value as 'open' | 'all')} className="w-40" aria-label="View">
          <option value="open">Open loans</option>
          <option value="all">All, incl. funded / closed</option>
        </Select>
        <Select value={stage} onChange={(e) => setStage(e.target.value)} className="w-52" aria-label="Stage">
          <option value="">Any stage</option>
          {LENDER_STAGE_ORDER.map((s) => (
            <option key={s} value={s}>
              {LENDER_STAGE_LABELS[s]}
            </option>
          ))}
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search loan #, borrower, address, status" className="w-72" aria-label="Search" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5">Lender</th>
              <th className="px-4 py-2.5">Loan #</th>
              <th className="px-4 py-2.5">Borrower</th>
              <th className="px-4 py-2.5">Stage</th>
              <th className="px-4 py-2.5">Lock</th>
              <th className="px-4 py-2.5 text-right">Amount</th>
              <th className="px-4 py-2.5 text-right">Rate</th>
              <th className="px-4 py-2.5">Product</th>
              <th className="px-4 py-2.5">Closing</th>
              <th className="px-4 py-2.5">Updated</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!listQuery.isLoading && sorted.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-slate-500">
                  {configuredCount === 0 ? 'No lender portal login is saved yet — add the EMMA or POWER+ login under Integrations and the pipelines appear here within the hour.' : 'No loans match.'}
                </td>
              </tr>
            )}
            {sorted.map((loan) => (
              <tr key={loan.id} className={clsx('cursor-pointer border-b border-slate-100 hover:bg-brand-navy-50/40', !loan.active && 'opacity-60')} onClick={() => setOpenId(loan.id)}>
                <td className="px-4 py-2.5 whitespace-nowrap font-medium text-slate-700">{lenderName(loan.lenderKey)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-slate-700">{loan.loanNumber ?? '—'}</td>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-slate-800">{loan.borrowerName ?? '—'}</div>
                  <div className="text-xs text-slate-500">{[loan.propertyCity, loan.propertyState].filter(Boolean).join(', ')}</div>
                </td>
                <td className="px-4 py-2.5">
                  <Badge tone={STAGE_TONE[loan.stage]}>{LENDER_STAGE_LABELS[loan.stage]}</Badge>
                  {loan.status && <div className="mt-0.5 text-xs text-slate-500">{loan.status}</div>}
                  {!loan.active && <div className="text-xs text-amber-700">no longer listed</div>}
                </td>
                <td className="px-4 py-2.5">
                  <LockCell loan={loan} />
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{money(loan.loanAmount)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{pct(loan.loanRate)}</td>
                <td className="max-w-[16rem] truncate px-4 py-2.5 text-slate-600" title={loan.loanProduct ?? undefined}>
                  {loan.loanProduct ?? '—'}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap">{dateOnly(loan.closingAt)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap text-xs text-slate-500">{fmtCT(loan.statusAt ?? loan.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {events.length > 0 && (
        <div className="border-t border-slate-100 px-5 py-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recent changes</h4>
          <ul className="mt-1 divide-y divide-slate-100">
            {events.slice(0, 12).map((e) => (
              <EventLine key={e.id} e={e} withLoan />
            ))}
          </ul>
        </div>
      )}
      {openId && <LoanDrawer id={openId} onClose={() => setOpenId(null)} />}
    </Card>
  );
}
